import type { RefObject } from "react";

import { useEffect, useRef } from "react";

type UseInfiniteScrollArgs = {
	fetchNextPage: () => void;
	hasNextPage: boolean;
	isFetching: boolean;
};

/** Calls `fetchNextPage` when the returned sentinel ref scrolls into view. */
export function useInfiniteScroll<T extends HTMLElement>({
	fetchNextPage,
	hasNextPage,
	isFetching,
}: UseInfiniteScrollArgs): RefObject<null | T> {
	const sentinelRef = useRef<T>(null);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasNextPage) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(sentinel);
		return () => {
			return observer.disconnect();
		};
	}, [fetchNextPage, hasNextPage, isFetching]);

	return sentinelRef;
}
